// "Give it a job": a Number, Chart, List, Table or custom widget filled by the
// user's own Claude Code or Codex, on their own computer, every day, every week
// or when they press Run now. The job is the widget's props.auto, as a brief's
// is: only the user sets it (the board refuses an agent), and a shared board
// shows what it wrote, never the job.

import { BRIEF_DEFAULT, canHaveJob, cleanAuto, type BriefAuto } from "../../shared/brief";
import { slugifyKey } from "../../shared/ids";
import type { WidgetItem } from "../../shared/types";
import { updateItem } from "../state/actions";
import { get, set, useStore } from "../state/store";

export const localZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** The widget's job, or null when it has none. */
export function jobOf(w: WidgetItem): BriefAuto | null {
  return canHaveJob(w.type) ? cleanAuto(w.props.auto) : null;
}

/**
 * Whether a widget can be given a job now: one of the kinds that can, with no job yet, not filled by a source
 * (a source would overwrite what the job writes), and for a custom widget, code that reads its data.
 */
export function jobOffered(w: WidgetItem): boolean {
  if (!canHaveJob(w.type) || jobOf(w)) return false;
  if (w.stream && get().streams[w.stream]?.pullId) return false;
  return w.type !== "custom" || !!w.stream || /board\.(data|onData)\b/.test(String(w.props.html ?? ""));
}

/** When the widget's brief or job began running on the user's computer, or 0 when it is not running. A shared view never knows. */
export function useJobRunning(id: string): number {
  return useStore((s) => s.jobsRunning[id] ?? 0);
}

/** Opens the widget's settings, where its job is. */
export function openJob(id: string) {
  set({ selection: [id], inspect: true, editing: null });
}

/** Gives the widget a job that runs only when the user presses Run now, until they choose a day or a week. It writes to the widget's own stream. */
export function giveJob(w: WidgetItem) {
  const auto: BriefAuto = { ...BRIEF_DEFAULT, on: true, tz: localZone(), every: "manual", prompt: "", from: Date.now() };
  updateItem(w.id, { stream: w.stream ?? slugifyKey(`job-${w.id.slice(-6)}`), props: { ...w.props, auto } }, "Give it a job");
  openJob(w.id);
}

/** Takes the job away: the widget keeps the data it has. */
export function removeJob(w: WidgetItem) {
  const { auto: _auto, ...props } = w.props;
  updateItem(w.id, { props }, "Remove its job");
}

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** When it runs, in a few words: "Every day at 08:00", "Mondays at 08:00" or "When you press Run now". */
export function jobWhen(a: BriefAuto): string {
  if (a.every === "manual") return "When you press Run now";
  return a.every === "week" ? `${DAYS[a.day]}s at ${a.time}` : `Every day at ${a.time}`;
}
